import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert,
  TextInput,
  ActivityIndicator,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import { logout } from '@/store/slices/authSlice';
import {
  getEmployeeProfile,
  updateEmployeeContact,
  UpdateContactPayload,
} from '@/store/slices/employeeSlice';
import AboutModal from '@/components/AboutModal';
import HelpSupportModal from '@/components/HelpSupportModal';

const ProfileScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { currentEmployee, isLoading } = useSelector((state: RootState) => state.employee);

  const [aboutModalVisible, setAboutModalVisible] = useState(false);
  const [helpModalVisible, setHelpModalVisible] = useState(false);

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<UpdateContactPayload>({});
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(
    null
  );

  useEffect(() => {
    if (user?.employee?.id) {
      dispatch(getEmployeeProfile(user.employee.id));
    }
  }, [dispatch, user?.employee?.id]);

  useEffect(() => {
    if (!feedback) return undefined;
    const id = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(id);
  }, [feedback]);

  const startEdit = () => {
    setForm({
      phone: currentEmployee?.phone ?? '',
      address: currentEmployee?.address ?? '',
      city: currentEmployee?.city ?? '',
      zip: currentEmployee?.zip ?? '',
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await dispatch(updateEmployeeContact(form)).unwrap();
      setEditing(false);
      setFeedback({ type: 'success', text: 'Contact info updated.' });
    } catch (e) {
      setFeedback({ type: 'error', text: typeof e === 'string' ? e : 'Could not save changes.' });
    } finally {
      setSaving(false);
    }
  };

  const renderField = (
    label: string,
    key: keyof UpdateContactPayload,
    icon: string,
    keyboardType: 'default' | 'phone-pad' | 'number-pad' = 'default'
  ) => (
    <View style={styles.fieldRow}>
      <Icon name={icon} size={20} color="#666666" />
      <View style={styles.fieldBody}>
        <Text style={styles.fieldLabel}>{label}</Text>
        <TextInput
          style={styles.input}
          value={form[key] ?? ''}
          onChangeText={(t) => setForm((f) => ({ ...f, [key]: t }))}
          placeholder={label}
          placeholderTextColor="#B0B0B0"
          keyboardType={keyboardType}
          autoCapitalize={key === 'city' || key === 'address' ? 'words' : 'none'}
        />
      </View>
    </View>
  );

  const handleLogout = () => {
    Alert.alert(
      'Logout',
      'Are you sure you want to logout?',
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Logout', onPress: () => dispatch(logout()) },
      ]
    );
  };

  return (
    <ScrollView style={styles.container}>
      {/* Profile Header */}
      <View style={styles.header}>
        <View style={styles.avatar}>
          <Text style={styles.avatarText}>
            {user?.employee?.firstName?.charAt(0)}{user?.employee?.lastName?.charAt(0)}
          </Text>
        </View>
        <Text style={styles.name}>{user?.employee?.fullName}</Text>
        <Text style={styles.email}>{currentEmployee?.email}</Text>
        {currentEmployee?.department && (
          <Text style={styles.department}>{currentEmployee.department.name}</Text>
        )}
      </View>

      {/* Contact Information (editable) */}
      <View style={styles.section}>
        <View style={styles.sectionHeaderRow}>
          <Text style={styles.sectionTitle}>Contact Information</Text>
          {!editing ? (
            <TouchableOpacity style={styles.editLink} onPress={startEdit}>
              <Icon name="create-outline" size={18} color="#007AFF" />
              <Text style={styles.editLinkText}>Edit</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity onPress={() => setEditing(false)} disabled={saving}>
              <Text style={styles.cancelLinkText}>Cancel</Text>
            </TouchableOpacity>
          )}
        </View>

        {feedback && (
          <View
            style={[
              styles.feedback,
              feedback.type === 'success' ? styles.feedbackOk : styles.feedbackErr,
            ]}>
            <Icon
              name={feedback.type === 'success' ? 'checkmark-circle' : 'alert-circle'}
              size={16}
              color={feedback.type === 'success' ? '#137333' : '#c5221f'}
            />
            <Text
              style={[
                styles.feedbackText,
                { color: feedback.type === 'success' ? '#137333' : '#c5221f' },
              ]}>
              {feedback.text}
            </Text>
          </View>
        )}

        {editing ? (
          <>
            {renderField('Phone', 'phone', 'call-outline', 'phone-pad')}
            {renderField('Street address', 'address', 'location-outline')}
            {renderField('City', 'city', 'business-outline')}
            {renderField('ZIP', 'zip', 'map-outline', 'number-pad')}
            <TouchableOpacity
              style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
              disabled={saving}
              onPress={saveEdit}>
              {saving ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.saveBtnText}>Save</Text>
              )}
            </TouchableOpacity>
            <Text style={styles.emailNote}>Email is managed by an admin and can't be edited here.</Text>
          </>
        ) : (
          <>
            {currentEmployee?.phone ? (
              <View style={styles.infoRow}>
                <Icon name="call-outline" size={20} color="#666666" />
                <Text style={styles.infoText}>{currentEmployee.phone}</Text>
              </View>
            ) : null}

            {currentEmployee?.email ? (
              <View style={styles.infoRow}>
                <Icon name="mail-outline" size={20} color="#666666" />
                <Text style={styles.infoText}>{currentEmployee.email}</Text>
              </View>
            ) : null}

            {currentEmployee?.address ? (
              <View style={styles.infoRow}>
                <Icon name="location-outline" size={20} color="#666666" />
                <Text style={styles.infoText}>
                  {currentEmployee.address}
                  {currentEmployee.city && `, ${currentEmployee.city}`}
                  {currentEmployee.zip && ` ${currentEmployee.zip}`}
                </Text>
              </View>
            ) : null}

            {!currentEmployee?.phone && !currentEmployee?.address ? (
              <Text style={styles.emptyContact}>No contact info yet — tap Edit to add it.</Text>
            ) : null}
          </>
        )}
      </View>

      {/* Employment Details */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Employment</Text>
        
        <View style={styles.infoRow}>
          <Icon name="briefcase-outline" size={20} color="#666666" />
          <Text style={styles.infoText}>
            {currentEmployee?.canTeach ? 'Instructor' : 'Staff Member'}
          </Text>
        </View>
        
        {currentEmployee?.startDate && (
          <View style={styles.infoRow}>
            <Icon name="calendar-outline" size={20} color="#666666" />
            <Text style={styles.infoText}>
              Started: {new Date(currentEmployee.startDate).toLocaleDateString()}
            </Text>
          </View>
        )}
        
        <View style={styles.infoRow}>
          <Icon name="checkmark-circle-outline" size={20} color="#34C759" />
          <Text style={styles.infoText}>Active Employee</Text>
        </View>
      </View>

      {/* Emergency Contact */}
      {currentEmployee?.emergencyContactName && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Emergency Contact</Text>
          
          <View style={styles.infoRow}>
            <Icon name="person-outline" size={20} color="#666666" />
            <Text style={styles.infoText}>{currentEmployee.emergencyContactName}</Text>
          </View>
          
          {currentEmployee.emergencyContactPhone && (
            <View style={styles.infoRow}>
              <Icon name="call-outline" size={20} color="#666666" />
              <Text style={styles.infoText}>{currentEmployee.emergencyContactPhone}</Text>
            </View>
          )}
          
          {currentEmployee.emergencyContactRelationship && (
            <View style={styles.infoRow}>
              <Icon name="heart-outline" size={20} color="#666666" />
              <Text style={styles.infoText}>{currentEmployee.emergencyContactRelationship}</Text>
            </View>
          )}
        </View>
      )}

      {/* Actions */}
      <View style={styles.section}>
        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => Alert.alert('Settings', 'Settings feature coming soon!')}>
          <Icon name="settings-outline" size={20} color="#007AFF" />
          <Text style={styles.actionText}>Settings</Text>
          <Icon name="chevron-forward" size={20} color="#C7C7CC" />
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => setHelpModalVisible(true)}>
          <Icon name="help-circle-outline" size={20} color="#007AFF" />
          <Text style={styles.actionText}>Help & Support</Text>
          <Icon name="chevron-forward" size={20} color="#C7C7CC" />
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.actionButton}
          onPress={() => setAboutModalVisible(true)}>
          <Icon name="information-circle-outline" size={20} color="#007AFF" />
          <Text style={styles.actionText}>About</Text>
          <Icon name="chevron-forward" size={20} color="#C7C7CC" />
        </TouchableOpacity>
      </View>

      {/* Logout */}
      <View style={styles.section}>
        <TouchableOpacity style={styles.logoutButton} onPress={handleLogout}>
          <Icon name="log-out-outline" size={20} color="#FF3B30" />
          <Text style={styles.logoutText}>Logout</Text>
        </TouchableOpacity>
      </View>

      {/* Modals */}
      <AboutModal
        visible={aboutModalVisible}
        onClose={() => setAboutModalVisible(false)}
      />
      <HelpSupportModal
        visible={helpModalVisible}
        onClose={() => setHelpModalVisible(false)}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8F8F8',
  },
  header: {
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    paddingVertical: 32,
    paddingHorizontal: 24,
  },
  avatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#007AFF',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  avatarText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  name: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 4,
  },
  email: {
    fontSize: 16,
    color: '#666666',
    marginBottom: 8,
  },
  department: {
    fontSize: 16,
    color: '#007AFF',
    fontWeight: '600',
  },
  section: {
    backgroundColor: '#FFFFFF',
    marginTop: 16,
    paddingHorizontal: 24,
    paddingVertical: 20,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: 16,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  infoText: {
    fontSize: 16,
    color: '#333333',
    marginLeft: 12,
    flex: 1,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  editLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  editLinkText: {
    fontSize: 15,
    color: '#007AFF',
    fontWeight: '600',
  },
  cancelLinkText: {
    fontSize: 15,
    color: '#8E8E93',
    fontWeight: '600',
  },
  feedback: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  feedbackOk: { backgroundColor: '#e6f4ea' },
  feedbackErr: { backgroundColor: '#fce8e6' },
  feedbackText: { fontSize: 14, fontWeight: '600', flex: 1 },
  fieldRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 14,
  },
  fieldBody: { flex: 1, marginLeft: 12 },
  fieldLabel: {
    fontSize: 12,
    color: '#888888',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  input: {
    fontSize: 16,
    color: '#333333',
    borderWidth: 1,
    borderColor: '#E0E0E0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#FAFAFA',
  },
  saveBtn: {
    backgroundColor: '#007AFF',
    borderRadius: 10,
    paddingVertical: 13,
    alignItems: 'center',
    marginTop: 4,
  },
  saveBtnDisabled: { opacity: 0.6 },
  saveBtnText: { color: '#FFFFFF', fontSize: 16, fontWeight: '600' },
  emailNote: {
    fontSize: 12,
    color: '#999999',
    marginTop: 10,
    textAlign: 'center',
  },
  emptyContact: {
    fontSize: 15,
    color: '#999999',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F0F0F0',
  },
  actionText: {
    fontSize: 16,
    color: '#333333',
    marginLeft: 12,
    flex: 1,
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
  },
  logoutText: {
    fontSize: 16,
    color: '#FF3B30',
    marginLeft: 12,
    fontWeight: '600',
  },
});

export default ProfileScreen;